// WARNING: This file exposes the SendGrid API key in the frontend. DO NOT use this in production!
import {
  InvitationEmailData,
  WeeklyDigestEmailData,
  UnlockRequestEmailData,
  PermissionChangeEmailData
} from '../types/trustedAdvisor';

const SENDGRID_API_KEY = import.meta.env.VITE_SENDGRID_API_KEY;
const FROM_EMAIL = import.meta.env.VITE_FROM_EMAIL || 'noreply@idecide.co';
const APP_URL = import.meta.env.VITE_APP_URL || 'https://idecide.co';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export class SendGridService {
  private static getApiUrl(endpoint: string): string {
    // For development, show a message that email functionality requires deployment
    if (import.meta.env.DEV) {
      throw new Error('Email functionality is not available in development mode. Please deploy to Cloudflare Pages to test email features.');
    }
    
    // For production, use the Cloudflare Pages API
    return `${window.location.origin}/api/${endpoint}`;
  }

  // Send general email using Cloudflare Workers API
  static async sendEmail(to: string, subject: string, htmlContent: string, textContent?: string): Promise<void> {
    try {
      const response = await fetch(this.getApiUrl('send-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to,
          subject,
          htmlContent,
          textContent,
          emailType: 'general'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send email');
      }

      const result = await response.json();
      console.log('Email sent successfully:', result);
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  }

  // Send Trusted Advisor invitation email
  static async sendTrustedAdvisorInvitation(data: InvitationEmailData): Promise<void> {
    try {
      const response = await fetch(this.getApiUrl('send-trusted-advisor-invitation'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          advisorEmail: data.advisor_name, // This should be the email address
          advisorName: data.advisor_name,
          userName: data.user_name,
          invitationMessage: data.invitation_message,
          invitationLink: data.invitation_link,
          expiresAt: data.expires_at
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send trusted advisor invitation');
      }

      const result = await response.json();
      console.log('Trusted advisor invitation sent successfully:', result);
    } catch (error) {
      console.error('Error sending trusted advisor invitation:', error);
      throw error;
    }
  }

  // Send invitation accepted notification
  static async sendInvitationAcceptedNotification(
    userEmail: string,
    userName: string,
    advisorName: string
  ): Promise<void> {
    try {
      const response = await fetch(this.getApiUrl('send-invitation-accepted-notification'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail,
          userName,
          advisorName
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invitation accepted notification');
      }

      const result = await response.json();
      console.log('Invitation accepted notification sent successfully:', result);
    } catch (error) {
      console.error('Error sending invitation accepted notification:', error);
      throw error;
    }
  }

  // Send unlock request notification
  static async sendUnlockRequestNotification(data: UnlockRequestEmailData): Promise<void> {
    try {
      const response = await fetch(this.getApiUrl('send-unlock-request-notification'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: data.user_name, // This should be the email address
          userName: data.user_name,
          advisorName: data.advisor_name,
          documentName: data.document_name,
          documentType: data.document_type,
          requestedAt: data.requested_at,
          unlockWaitPeriodDays: data.unlock_wait_period_days,
          blockLink: data.block_link,
          approveLink: data.approve_link
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send unlock request notification');
      }

      const result = await response.json();
      console.log('Unlock request notification sent successfully:', result);
    } catch (error) {
      console.error('Error sending unlock request notification:', error);
      throw error;
    }
  }

  // Send weekly digest email
  static async sendWeeklyDigest(data: WeeklyDigestEmailData): Promise<void> {
    try {
      const response = await fetch(this.getApiUrl('send-weekly-digest'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: data.user_name, // This should be the email address
          userName: data.user_name,
          digestData: data.digest_data,
          dashboardLink: data.dashboard_link
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send weekly digest');
      }

      const result = await response.json();
      console.log('Weekly digest sent successfully:', result);
    } catch (error) {
      console.error('Error sending weekly digest:', error);
      throw error;
    }
  }

  // Send welcome onboarding email
  static async sendWelcomeOnboardingEmail(userEmail: string, userName: string): Promise<void> {
    try {
      const response = await fetch(this.getApiUrl('send-welcome-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail,
          userName
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send welcome email');
      }

      const result = await response.json();
      console.log('Welcome email sent successfully:', result);
    } catch (error) {
      console.error('Error sending welcome email:', error);
      throw error;
    }
  }

  // Send permission change notification email
  static async sendPermissionChangeNotification(data: PermissionChangeEmailData): Promise<void> {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Permission Change - iDecide</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #2563eb;">Permission Change Notification</h2>
          <p>Hello ${data.user_name},</p>
          <p>Your permissions for <strong>${data.document_name}</strong> have been updated by <strong>${data.advisor_name}</strong>.</p>
          
          <div style="background-color: #f3f4f6; padding: 15px; border-radius: 6px; margin: 20px 0;">
            <h3>Changes Made:</h3>
            <p><strong>Permission:</strong> ${data.old_permission} → ${data.new_permission}</p>
            <p><strong>View Mode:</strong> ${data.old_view_mode} → ${data.new_view_mode}</p>
            <p><strong>Changed on:</strong> ${new Date(data.changed_at).toLocaleDateString()}</p>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${import.meta.env.VITE_APP_URL || 'https://idecide.co'}/dashboard" 
               style="background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
              View Dashboard
            </a>
          </div>
          <p>Best regards,<br>The iDecide Team</p>
        </div>
      </body>
      </html>
    `;

    await this.sendEmail(
      data.user_name, // This should be the email address
      'Permission Change - iDecide',
      htmlContent
    );
  }

  // Send emergency contact notification
  static async sendEmergencyContactNotification(
    contactEmail: string,
    contactName: string,
    userName: string,
    message: string
  ): Promise<void> {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Emergency Contact Notification - iDecide</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #dc2626;">Emergency Contact Notification</h2>
          <p>Hello ${contactName},</p>
          <p>This is an emergency notification from <strong>${userName}</strong> via iDecide.</p>
          
          <div style="background-color: #fef2f2; padding: 15px; border-radius: 6px; margin: 20px 0; border-left: 4px solid #dc2626;">
            <h3>Message:</h3>
            <p>${message}</p>
          </div>
          
          <p>Please respond to this notification as soon as possible.</p>
          <p>Best regards,<br>The iDecide Team</p>
        </div>
      </body>
      </html>
    `;

    await this.sendEmail(
      contactEmail,
      'Emergency Contact Notification - iDecide',
      htmlContent
    );
  }

  // Send test email
  static async sendTestEmail(testEmail: string): Promise<void> {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Test Email - iDecide</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #2563eb;">Test Email from iDecide</h2>
          <p>Hello!</p>
          <p>This is a test email to verify that your SendGrid configuration is working correctly.</p>
          <p>If you received this email, your email system is properly configured!</p>
          <p>Best regards,<br>The iDecide Team</p>
        </div>
      </body>
      </html>
    `;

    await this.sendEmail(
      testEmail,
      'Test Email - iDecide SendGrid Configuration',
      htmlContent
    );
  }

  // Send bulk email
  static async sendBulkEmail(
    recipients: string[],
    subject: string,
    htmlContent: string,
    textContent?: string
  ): Promise<void> {
    // For bulk emails, we'll send them individually to avoid rate limits
    const promises = recipients.map(recipient => 
      this.sendEmail(recipient, subject, htmlContent, textContent)
    );
    
    await Promise.all(promises);
  }

  // Test SendGrid connection
  static async testConnection(): Promise<boolean> {
    try {
      // Try to send a test email to verify connection
      await this.sendTestEmail('test@example.com');
      return true;
    } catch (error) {
      console.error('SendGrid connection test failed:', error);
      return false;
    }
  }

  // Validate email format
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Sanitize email content
  static sanitizeEmailContent(content: string): string {
    // Basic sanitization - remove potentially dangerous HTML
    return content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  }

  // Legacy method for backward compatibility
  static async sendMail({ to, subject, text, html }: { to: string; subject: string; text: string; html?: string }) {
    await this.sendEmail(to, subject, html || text, text);
  }
} 